import { To } from 'react-router-dom';

import { PathName } from 'router/paths';

/**
 * What the patient's programs say about the flows they can still be sent into.
 *
 * `flowUrl` only records the funnel a patient arrived from; it says nothing about what they hold
 * now. A destination derived from it alone can be one the destination's own guard turns away, and
 * because the redirect below is re-applied on every route the patient lands on, being turned away
 * means being sent straight back. Read from `/my-programs`, which already names each program's
 * category — no new field, and the same shape extends to program types added later.
 */
export interface FlowEligibility {
  /** Any weight-management program, Active or Inactive. See `hasWeightManagementProgram`. */
  hadOrHasWeightManagement: boolean;
}

/**
 * Whether this URL's destination is one `FlowEligibility` decides, so a caller knows it must not
 * answer before the patient's programs are known. Derived from `getFlowByUrl` itself rather than
 * re-matching the URL, so the two cannot drift.
 */
export const flowNeedsProgramEligibility = (url: string): boolean =>
  getFlowByUrl(url) === PathName.MigrateToWM;

export const getFlowByUrl = (url: string, eligibility?: FlowEligibility): To => {
  const flowToComplete = (v: string) => {
    if (v.includes('mental-health')) return 'mental-health';
    if (v.includes('wm') || v.includes('savvy')) return 'weight-management';
    return 'lifemd-plus';
  };
  const flow = flowToComplete(url);
  return getRedirectPathByFlowType(flow, eligibility);
};

const getRedirectPathByFlowType = (flowType: string, eligibility?: FlowEligibility): To => {
  switch (flowType) {
    case 'mental-health':
      return { pathname: PathName.Join, search: 'f=mental-health' };
    case 'weight-management':
      /*
       * `MigrateToWMGuard` turns away anyone who has or had a weight-management program — the flow
       * exists to sell them one. Asking the same question here is what keeps the two from
       * disagreeing: a patient it would reject is sent to the dashboard instead, which is a
       * destination with no guard of its own and therefore terminates.
       *
       * Of the three destinations this function returns, only `/migrate-to-wm` sits inside
       * `Router`'s own route subtree, so it is the only one the forced redirect can bounce off.
       * `/join` and `/create-appointment` are mounted above `Router` and never re-enter it.
       */
      return eligibility?.hadOrHasWeightManagement ? PathName.Home : PathName.MigrateToWM;
    case 'lifemd-plus':
    default:
      return {
        pathname: PathName.CreateAppointment
      };
  }
};

export const getCompleteFlowByParam = (v: string): To => {
  switch (v) {
    case 'mental-health':
      return { pathname: PathName.Join, search: 'f=mental-health&s=results' };
    case 'weight-management':
      return { pathname: PathName.MigrateToWM };
    default:
      return { pathname: PathName.CreateAppointment };
  }
};
